<script setup>
import { Heading } from "~/components/clarity-ui/Heading";
import { Paragraph } from "~/components/clarity-ui/Paragraph";
import { ParallaxContainer } from "~/components/ui/ParallaxContainer";
import PictureElement from "~/templates/elements/PictureElement";
import CallToActionComponent from "~/templates/components/cms/CallToActionComponent";
import { contentToRatio, DIMENSIONS } from "~/utils/helper/imaging";
import { containsImage } from "~/utils/helper/magnolia";
import { contentToTailwindClasses } from "~/utils/helper/tailwind";

defineProps(["headline", "subline", "image", "imageMobile", "link", "opacity", "stretchLink"]);

const ratio = inject("ratio");

const BREAKPOINTS = {
  start: DIMENSIONS["start"]?.ratio["3/4"]?.scale["1.5"],
  sm: DIMENSIONS["sm"].ratio["3/4"]?.scale["1.5"],
  md: DIMENSIONS["md"].width["1/2"]?.ratio["3/4"]?.scale["1.5"],
  xl: DIMENSIONS["xl"].width["1/2"]?.ratio[contentToRatio(ratio ?? "square")]?.scale["1.5"],
};
</script>

<template>
  <div class="group relative overflow-hidden rounded-2xl">
    <ParallaxContainer class="relative inset-0">
      <div
        :class="ratio === 'postcard' ? 'xl:aspect-3/2' : 'xl:aspect-square'"
        class="aspect-3/4 group-hover:scale-110 motion-safe:transition-transform motion-safe:duration-300"
      >
        <PictureElement
          :breakpoints="BREAKPOINTS"
          :class="{
            hidden: containsImage(imageMobile),
            'xl:inline': containsImage(imageMobile),
          }"
          :img="image"
          img-class="block object-cover w-full h-full"
        />
        <PictureElement
          v-if="containsImage(imageMobile)"
          :breakpoints="BREAKPOINTS"
          :img="imageMobile"
          class="xl:hidden"
          img-class="block object-cover w-full h-full"
        />
      </div>
    </ParallaxContainer>
    <div
      :class="contentToTailwindClasses({ opacity: opacity ?? 75 })"
      class="absolute inset-x-0 top-0 h-1/3 bg-gradient-to-b from-black to-transparent"
    />
    <div
      :class="contentToTailwindClasses({ opacity: opacity ?? 75 })"
      class="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black to-transparent"
    />
    <div class="absolute inset-0 flex flex-col items-start justify-between p-6 xl:p-10">
      <div class="flex flex-col gap-4">
        <Heading v-if="headline?.text" color="light" size="large" :tag="headline?.tag ?? 'h3'">{{
          headline?.text
        }}</Heading>
        <Paragraph class="text-white">{{ subline }}</Paragraph>
      </div>
      <CallToActionComponent :link="link" :stretch-link="stretchLink" />
    </div>
  </div>
</template>
